import { Button, PageHeader, Form, EmptyState } from '@lmc/ui-kit'
import { NAMESPACE } from 'locale'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { useTranslation } from 'react-i18next'
import { Form as AntdForm } from 'antd'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ProfileWrapper,
  ButtonsWrapper,
  ProfileContent
} from '../../styledComponents'
import { useEffect, useRef, useState } from 'react'
import { getAge, handleOnFinishFailed, scrollToTop } from '../../utils/utils'
import { Home, ResidentBody, UserRole } from '@lmc/ts-common'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { NameFormCard } from './FormCards/Name'
import { DemographicFormCard } from './FormCards/Demographic'
import { CarerPreferenceFormCard } from './FormCards/CarerPreference'
import { PersonalNotesFormCard } from './FormCards/PersonalNotes'
import useResident, { convertFormTypeToResidentBody } from 'hooks/useResident'
import { getNominative, showToast } from 'common/utils'
import { Spinner } from 'components/generic'
import dayjs from 'dayjs'
import useHomeType from 'hooks/useHomeType'
import EditProfilePictureFormCard from './FormCards/EditProfilePicture'
import { useSelector } from 'react-redux'
import { generalConstants } from 'locale/general'
import NavigationModal from 'components/NavigationModal/NavigationModal'
import { useRoleAccess } from 'components/AccessControl'
import {
  ResidentFetchItemResponse,
  ResidentDemographicsUpdate,
  ResidentUpdateRequest,
  PregnancyStatusCode,
  SexualOrientationCode,
  MaritalStatusCode,
  ReligionCode
} from '@lmc/validations'

export type FormType = {
  dateOfBirth: dayjs.Dayjs
  demographics?: ResidentDemographicsUpdate
  pregnancyStatus?: PregnancyStatusCode
  sexualOrientation?: SexualOrientationCode
  maritalStatus?: MaritalStatusCode
  religion?: ReligionCode
}

export const ResidentPersonalInformation = ({
  testId
}: { testId?: string } = {}) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)
  const topRef = useRef<HTMLDivElement | null>(null)
  const [form] = AntdForm.useForm()
  const navigate = useNavigate()
  const [isDirty, setIsDirty] = useState(false)
  const { id } = useParams()
  const preferredPeopleNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative
  )
  const residentNominative = getNominative(
    preferredPeopleNominative,
    'Resident'
  )
  const { resident, isLoading, updateResident } = useResident({
    id,
    onUpdateSuccess: () => {
      showToast({
        title: t(supConstants.RESIDENT_UPDATED, {
          variable: residentNominative
        })
      })
      navigate(`../profile`, { relative: 'path' })
    }
  })
  const [residentAge, setResidentAge] = useState(getAge(resident?.dateOfBirth))
  const [isUpdateLoading, setIsUpdateLoading] = useState(false)
  const { isPro } = useHomeType()
  const featureFlags = useFeatureFlags({
    flagNames: ['mods_demographics']
  })?.data
  const isModsDemographicsEnabled = featureFlags?.mods_demographics?.enabled

  const hasRolePermission = useRoleAccess(
    [UserRole.careHomeAdmin],
    [
      {
        i: 'update',
        this: 'Resident'
      }
    ]
  )

  const handleOnFinish = async (values: FormType) => {
    setIsUpdateLoading(true)

    try {
      const convertedValues = convertFormTypeToResidentBody(
        values,
        (vals) =>
          convertFormTypeToPartialResidentBody(
            vals,
            (resident as any)?.demographics
          ) as Partial<ResidentBody>
      )
      setIsDirty(false)
      await updateResident(convertedValues)
    } catch {
      showToast({
        title: t(generalConstants.ERROR, { ns: NAMESPACE.GENERAL }),
        type: 'error'
      })
    }
    setIsUpdateLoading(false)
  }

  const handleOnCancel = () => {
    navigate(`../profile`, { relative: 'path' })
  }

  const handleFormValuesChange = (
    values: Partial<ResidentBody>,
    changedValues: Partial<ResidentBody>
  ) => {
    if (changedValues.dateOfBirth) {
      const age = getAge(changedValues.dateOfBirth)
      setResidentAge(age)
    }
    if (values) {
      setIsDirty(true)
    }
  }

  useEffect(() => {
    scrollToTop(topRef)
  }, [])

  if (isLoading) {
    return (
      <Spinner
        data-testid={
          testId ? `${testId}-personal-info-loading` : 'personal-info-loading'
        }
      />
    )
  }

  if (resident === undefined) {
    return (
      <EmptyState
        title={t(supConstants.NO_RESIDENT)}
        data-testid={
          testId
            ? `${testId}-personal-info-no-resident`
            : 'personal-info-no-resident'
        }
      />
    )
  }

  const residentWithDemographics = resident as ResidentFetchItemResponse
  const initialValues: Partial<FormType> = {
    ...resident,
    dateOfBirth: dayjs(resident?.dateOfBirth) || undefined,
    ...(isModsDemographicsEnabled && residentWithDemographics?.demographics
      ? {
          demographics: {
            genderIdentity:
              residentWithDemographics.demographics.genderIdentity ?? undefined,
            sex: residentWithDemographics.demographics.sex ?? undefined
          }
        }
      : {}),
    pregnancyStatus:
      (resident as any)?.demographics?.pregnancyStatus ?? undefined,
    sexualOrientation:
      (resident as any)?.demographics?.sexualOrientation ?? undefined,
    maritalStatus: (resident as any)?.demographics?.maritalStatus ?? undefined,
    religion: (resident as any)?.demographics?.religion ?? undefined
  }

  return (
    <ProfileWrapper
      ref={topRef}
      data-testid={
        testId ? `${testId}-personal-info-wrapper` : 'personal-info-wrapper'
      }
    >
      <PageHeader
        sticky
        title={t(supConstants.PERSONAL_INFORMATION)}
        data-testid={
          testId ? `${testId}-personal-info-header` : 'personal-info-header'
        }
      >
        <ButtonsWrapper
          data-testid={
            testId ? `${testId}-personal-info-buttons` : 'personal-info-buttons'
          }
        >
          <Button
            data-cy="cancel-btn"
            onClick={handleOnCancel}
            data-testid={
              testId
                ? `${testId}-personal-info-cancel-btn`
                : 'personal-info-cancel-btn'
            }
          >
            {t(supConstants.CANCEL)}
          </Button>
          <Button
            data-cy="submit-btn"
            variant="primary"
            onClick={form.submit}
            type="submit"
            isLoading={isUpdateLoading}
            disabledByPermission={!hasRolePermission}
            data-testid={
              testId
                ? `${testId}-personal-info-save-btn`
                : 'personal-info-save-btn'
            }
          >
            {t(supConstants.SAVE)}
          </Button>
        </ButtonsWrapper>
      </PageHeader>
      <ProfileContent
        data-testid={
          testId ? `${testId}-personal-info-content` : 'personal-info-content'
        }
      >
        <NavigationModal canNavigate={!isDirty} />
        <EditProfilePictureFormCard />
        <Form<FormType>
          form={form}
          onFinish={handleOnFinish}
          onValuesChange={handleFormValuesChange}
          initialValues={initialValues}
          onFinishFailed={({ errorFields }) =>
            handleOnFinishFailed(errorFields)
          }
          data-testid={
            testId ? `${testId}-personal-info-form` : 'personal-info-form'
          }
        >
          <NameFormCard />
          <DemographicFormCard
            residentAge={residentAge}
            isPro={isPro}
            resident={resident as ResidentFetchItemResponse}
          />
          {isPro && <CarerPreferenceFormCard />}
          <PersonalNotesFormCard />
        </Form>
      </ProfileContent>
    </ProfileWrapper>
  )
}

export const convertFormTypeToPartialResidentBody = (
  values: FormType,
  currentDemographics?: Record<string, unknown>
): Partial<ResidentUpdateRequest> => {
  const {
    demographics,
    pregnancyStatus,
    sexualOrientation,
    maritalStatus,
    religion,
    ...rest
  } = values as FormType & {
    pregnancyStatus?: string
    sexualOrientation?: string
    maritalStatus?: string
    religion?: string
  }

  return {
    ...rest,
    // we need to send the date in the format 'DD-MM-YYYY' because of some legacy body parser code in BE
    dateOfBirth: values?.dateOfBirth.format('DD-MM-YYYY'),
    demographics: {
      ...currentDemographics,
      ...demographics,
      pregnancyStatus: pregnancyStatus ?? null,
      sexualOrientation: sexualOrientation ?? null,
      maritalStatus: maritalStatus ?? null,
      religion: religion ?? null
    }
  }
}
